import React, { useEffect, useState } from 'react';

import { Button, Image } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import PropTypes from 'prop-types';
import SplitPane from 'react-split-pane';

import {
  anchovy,
  backgroundGrey,
  blackberry,
  grape,
  greyBorder,
  mediumGray,
  mutedGray,
  purple1,
  shadowMedium,
  shadowVeryLight,
  standardGray,
  white,
} from 'lib/css/colors';
import AttributeGroupView from 'components/l4_segmentation/AttributeGroupView';
import { confirmRichAction, SwalToast } from 'lib/utils/toast';
import {
  L4_CATEGORY_DIVISION_ATTRIBUTE_SUMMARY,
  L4_CATEGORY_DIVISION_RETRY_SEGMENTATION,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import useGet from 'lib/hooks/useGet';
import useL4SegmentationUrlParams from 'lib/hooks/useL4SegmentationUrlParams';
import usePost from 'lib/hooks/usePost';

function L4SegmentationDetailView({ category, onBack }) {
  const { params, replaceParams } = useL4SegmentationUrlParams();
  const [selectedAttribute, setSelectedAttribute] = useState(() => params.attr);

  const {
    data: {
      data: attributeSummary = [],
      total_products: totalProducts = 0,
      category_hierarchy: categoryHierarchy = null,
    } = {},
    loading,
  } = useGet(L4_CATEGORY_DIVISION_ATTRIBUTE_SUMMARY, {
    l4_category_id: category.id,
  });

  const { postData: retrySegmentation, loading: retryLoading } = usePost(
    L4_CATEGORY_DIVISION_RETRY_SEGMENTATION,
    response =>
      SwalToast('success', response?.data?.data || 'Retry segmentation task has been queued.'),
    error =>
      SwalToast('error', error?.response?.data?.error || 'Failed to start retry segmentation.'),
  );

  useEffect(() => {
    setSelectedAttribute(params.attr);
  }, [params.attr]);

  useEffect(() => {
    if (!attributeSummary.length || !params.attr) return;
    const exists = attributeSummary.some(a => a.attribute_type === params.attr);
    if (!exists) replaceParams({ attr: null });
  }, [attributeSummary, params.attr, replaceParams]);

  const handleAttributeClick = attrType => {
    setSelectedAttribute(attrType);
    replaceParams({ attr: attrType, value: null, page: null });
  };

  const handleRetrySegmentation = () => {
    const safeName = (category.category_name || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    confirmRichAction(
      () => retrySegmentation({ l4_category_id: category.id }),
      `This will delete all existing segmentation data for <b>${safeName}</b> ` +
        `and re-send products to the ML server for initial segmentation.`,
      'Retry Segmentation?',
      'Yes, retry segmentation',
    );
  };

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.header)}>
        <Button variant="outline-secondary" onClick={onBack} className="mr-3">
          &larr; Back to Categories
        </Button>
        <h4 className="mb-0 font-weight-bold flex-grow-1">{category.category_name}</h4>
        <Button
          onClick={handleRetrySegmentation}
          disabled={retryLoading}
          className={classNames(css(styles.retryBtn), 'ml-3')}
        >
          {retryLoading ? 'Processing...' : 'Retry Segmentation'}
        </Button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <SplitPane
          split="vertical"
          defaultSize="280px"
          minSize={220}
          maxSize={400}
          className={css(styles.splitPane)}
        >
          <div className={css(styles.leftPane)}>
            <div className={css(styles.categoryInfo)}>
              <Image
                src={category.default_image_url || placeholderProductImage}
                className={css(styles.categoryImage)}
              />
              <div className={css(styles.categoryName)}>{category.category_name}</div>
              <div className={css(styles.productCount)}>
                {totalProducts.toLocaleString()} products analyzed
              </div>
            </div>

            {categoryHierarchy && (
              <div className={css(styles.hierarchySection)}>
                <div className={css(styles.hierarchyTitle)}>Category Hierarchy</div>
                <div className={css(styles.hierarchyList)}>
                  {categoryHierarchy.l0 && (
                    <div className={css(styles.hierarchyItem)}>
                      <span className={css(styles.hierarchyLevel)}>L0</span>
                      <span className={css(styles.hierarchyName)}>{categoryHierarchy.l0.name}</span>
                    </div>
                  )}
                  {categoryHierarchy.l1 && (
                    <div className={css(styles.hierarchyItem)}>
                      <span className={css(styles.hierarchyLevel)}>L1</span>
                      <span className={css(styles.hierarchyName)}>{categoryHierarchy.l1.name}</span>
                    </div>
                  )}
                  {categoryHierarchy.l2 && (
                    <div className={css(styles.hierarchyItem)}>
                      <span className={css(styles.hierarchyLevel)}>L2</span>
                      <span className={css(styles.hierarchyName)}>{categoryHierarchy.l2.name}</span>
                    </div>
                  )}
                  {categoryHierarchy.l3 && (
                    <div className={css(styles.hierarchyItem)}>
                      <span className={css(styles.hierarchyLevel)}>L3</span>
                      <span className={css(styles.hierarchyName)}>{categoryHierarchy.l3.name}</span>
                    </div>
                  )}
                  {categoryHierarchy.l4 && (
                    <div className={css(styles.hierarchyItem, styles.hierarchyItemActive)}>
                      <span className={css(styles.hierarchyLevel)}>L4</span>
                      <span className={css(styles.hierarchyName)}>{categoryHierarchy.l4.name}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className={css(styles.attributeButtons)}>
              {attributeSummary.map(attr => (
                <Button
                  key={attr.attribute_type}
                  className={classNames(
                    css(
                      styles.attributeButton,
                      selectedAttribute === attr.attribute_type && styles.attributeButtonActive,
                    ),
                    'w-100 mb-2',
                  )}
                  onClick={() => handleAttributeClick(attr.attribute_type)}
                >
                  <span className={css(styles.attributeName)}>{attr.attribute_type}</span>
                  <span className={css(styles.attributeCount)}>{attr.total_count}</span>
                </Button>
              ))}
            </div>
          </div>

          <div className={css(styles.rightPane)}>
            {!selectedAttribute ? (
              <div className={css(styles.placeholder)}>
                Select an attribute from the left panel to view details
              </div>
            ) : (
              <AttributeGroupView
                key={`${category.id}-${selectedAttribute}`}
                attributeType={selectedAttribute}
                attributeData={attributeSummary.find(a => a.attribute_type === selectedAttribute)}
                category={category}
              />
            )}
          </div>
        </SplitPane>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    height: 'calc(100vh - 200px)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    borderBottom: `1px solid ${greyBorder}`,
    marginBottom: '16px',
  },
  retryBtn: {
    backgroundColor: blackberry,
    borderColor: blackberry,
    color: white,
    fontWeight: 500,
    ':disabled': {
      backgroundColor: blackberry,
      borderColor: blackberry,
      opacity: 0.65,
    },
  },
  splitPane: {
    position: 'relative',
    flex: 1,
    overflow: 'hidden',
  },
  leftPane: {
    height: '100%',
    padding: '16px',
    backgroundColor: backgroundGrey,
    overflowY: 'auto',
    borderRight: `1px solid ${greyBorder}`,
  },
  categoryInfo: {
    textAlign: 'center',
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: white,
    borderRadius: '8px',
    boxShadow: `0 2px 4px ${shadowVeryLight}`,
  },
  categoryImage: {
    width: '100px',
    height: '100px',
    objectFit: 'contain',
    borderRadius: '8px',
    marginBottom: '12px',
    backgroundColor: backgroundGrey,
    padding: '8px',
  },
  categoryName: {
    fontSize: '16px',
    fontWeight: 600,
    color: anchovy,
    marginBottom: '4px',
  },
  productCount: {
    fontSize: '14px',
    color: mediumGray,
  },
  attributeButtons: {
    display: 'flex',
    flexDirection: 'column',
  },
  attributeButton: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'left',
    fontSize: '13px',
    backgroundColor: 'transparent',
    borderColor: greyBorder,
    color: anchovy,
    ':hover': {
      backgroundColor: purple1,
      borderColor: purple1,
      color: white,
    },
  },
  attributeButtonActive: {
    backgroundColor: blackberry,
    borderColor: blackberry,
    color: white,
    ':hover': {
      backgroundColor: blackberry,
      borderColor: blackberry,
      color: white,
    },
  },
  attributeName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  attributeCount: {
    backgroundColor: shadowMedium,
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    marginLeft: '8px',
  },
  rightPane: {
    height: '100%',
    padding: '16px',
    overflowY: 'auto',
    backgroundColor: white,
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: standardGray,
    fontSize: '16px',
  },
  hierarchySection: {
    marginBottom: '20px',
    padding: '12px',
    backgroundColor: white,
    borderRadius: '8px',
    boxShadow: `0 2px 4px ${shadowVeryLight}`,
  },
  hierarchyTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: mutedGray,
    textTransform: 'uppercase',
    marginBottom: '10px',
    letterSpacing: '0.5px',
  },
  hierarchyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  hierarchyItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
    borderRadius: '4px',
    backgroundColor: backgroundGrey,
  },
  hierarchyItemActive: {
    backgroundColor: grape,
    color: white,
  },
  hierarchyLevel: {
    fontSize: '10px',
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: '3px',
    backgroundColor: shadowMedium,
    marginRight: '8px',
    minWidth: '24px',
    textAlign: 'center',
  },
  hierarchyName: {
    fontSize: '12px',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

L4SegmentationDetailView.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.number.isRequired,
    category_name: PropTypes.string.isRequired,
    default_image_url: PropTypes.string,
    product_count: PropTypes.number,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
};

export default L4SegmentationDetailView;
